import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/store";
import { useCan } from "../../auth/roles";
import { ErrorState } from "../../components/States";
import { settingsEqual, UnsavedGuard } from "../../components/UnsavedChanges";
import { BusyButton, ConfirmDialog, DataTable, Dialog, Field, RowMenu, useToast } from "../../components/ui";
import { backendApi, backendKeys, type AlertLevel, type MetricAgg, type MetricInfo, type MetricRule, type MetricRuleInput } from "./api";
import { AGG_LABEL, aggsFor, unitName } from "./components";

export const ALERT_LEVELS: Array<{ id: AlertLevel; label: string }> = [
  { id: "warning", label: "Warning" },
  { id: "error", label: "Error" },
  { id: "critical", label: "Critical" },
];
const WINDOWS = [1, 5, 10, 15, 30, 60];

export interface AlertDraft {
  name: string;
  metric: string;
  service: string;
  aggregation: MetricAgg;
  comparator: "above" | "below";
  threshold: string;
  windowMin: number;
  severity: AlertLevel;
  enabled: boolean;
}

export type AlertEditing = { id?: string; draft: AlertDraft; initial: AlertDraft };

export function newAlert(metric: MetricInfo, agg?: MetricAgg, service?: string): AlertEditing {
  const aggregation = agg ?? aggsFor(metric.kind)[0]!;
  const draft: AlertDraft = { name: "", metric: metric.name, service: service ?? "", aggregation, comparator: "above", threshold: "", windowMin: 5, severity: "warning", enabled: true };
  return { draft, initial: draft };
}

export function editAlert(r: MetricRule): AlertEditing {
  const draft: AlertDraft = { name: r.name, metric: r.metric, service: r.service, aggregation: r.aggregation, comparator: r.comparator, threshold: String(r.threshold), windowMin: r.windowMin, severity: r.severity, enabled: r.enabled };
  return { id: r.id, draft, initial: draft };
}

export function alertErrors(d: AlertDraft): Record<string, string> {
  const e: Record<string, string> = {};
  if (d.name.trim().length > 120) e.name = "Use 120 characters or fewer.";
  if (d.threshold.trim() !== "" && !Number.isFinite(Number(d.threshold))) e.threshold = "Enter a number.";
  return e;
}

export function toRuleInput(d: AlertDraft): MetricRuleInput {
  return { name: d.name.trim(), metric: d.metric, service: d.service, aggregation: d.aggregation, comparator: d.comparator, threshold: Number(d.threshold), windowMin: d.windowMin, severity: d.severity, enabled: d.enabled };
}

const BYTE_STEPS: Array<[number, string]> = [[1e12, "TB"], [1e9, "GB"], [1e6, "MB"], [1e3, "KB"]];

/** A metric value with its unit, as a person would say it ("1.2 GB", "800 ms", "42/s"). */
export function valueLabel(v: number, agg: MetricAgg, unit?: string): string {
  const num = (n: number) => Number(n.toPrecision(4)).toLocaleString();
  if (agg === "rate") return `${num(v)}/s`;
  if (agg === "count") return num(v);
  if (unit === "By") {
    const step = BYTE_STEPS.find(([s]) => Math.abs(v) >= s);
    return step ? `${num(v / step[0])} ${step[1]}` : `${num(v)} bytes`;
  }
  const u = unitName(unit);
  return u ? `${num(v)} ${u}` : num(v);
}

/** "95th percentile above 500 ms for 5 minutes" */
export function conditionLabel(r: Pick<MetricRule, "aggregation" | "comparator" | "threshold" | "windowMin">, unit?: string): string {
  const mins = r.windowMin === 1 ? "1 minute" : `${r.windowMin} minutes`;
  return `${AGG_LABEL[r.aggregation] ?? r.aggregation} ${r.comparator} ${valueLabel(r.threshold, r.aggregation, unit)} for ${mins}`;
}

export function MetricAlertDialog({ editing, setEditing, metrics }: { editing: AlertEditing | null; setEditing: (e: AlertEditing | null) => void; metrics: MetricInfo[] }) {
  const auth = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [serverErr, setServerErr] = useState<Record<string, string>>({});
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const save = useMutation({
    mutationFn: (e: AlertEditing) => {
      const body = toRuleInput(e.draft);
      return e.id ? backendApi.updateMetricRule(auth.currentProjectId!, e.id, body) : backendApi.createMetricRule(auth.currentProjectId!, body);
    },
    onSuccess: (_r, e) => {
      setEditing(null);
      setServerErr({});
      void qc.invalidateQueries({ queryKey: backendKeys.metricRules(auth.currentProjectId) });
      toast.push("success", e.id ? "Alert updated" : "Alert created");
    },
    onError: (err) => {
      const x = err as Error & { field?: string; body?: { field?: string } };
      const field = x.field ?? x.body?.field;
      if (field) setServerErr({ [field]: x.message });
      else toast.push("error", x.message);
    },
  });
  const d = editing?.draft;
  const dirty = !!editing && !settingsEqual(editing.draft, editing.initial);
  const errors = d ? { ...alertErrors(d), ...serverErr } : {};
  const ready = !!d && d.name.trim() !== "" && d.threshold.trim() !== "" && Object.keys(errors).length === 0 && dirty;
  const info = d ? metrics.find((m) => m.name === d.metric) : undefined;
  const aggs = info ? aggsFor(info.kind) : d ? [d.aggregation] : [];
  const services = info?.services ?? (d?.service ? [d.service] : []);
  const set = (patch: Partial<AlertDraft>) => {
    if (!editing) return;
    setServerErr({});
    setEditing({ ...editing, draft: { ...editing.draft, ...patch } });
  };
  const close = () => {
    if (save.isPending || confirmDiscard) return;
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    setEditing(null);
  };

  return (
    <>
      <Dialog
        open={!!editing}
        onClose={close}
        title={editing?.id ? "Edit metric alert" : "Alert on this metric"}
        subtitle={d ? <span className="mono">{d.metric}</span> : undefined}
        width={560}
        className="be-editor"
        footer={
          <>
            <button type="button" className="btn" onClick={close} disabled={save.isPending}>Cancel</button>
            <BusyButton className="btn primary" busy={save.isPending} busyLabel="Saving…" disabled={!ready} onClick={() => editing && save.mutate(editing)}>
              {editing?.id ? "Save alert" : "Create alert"}
            </BusyButton>
          </>
        }
      >
        {d && (
          <>
            <UnsavedGuard dirty={dirty} />
            <Field label="Name" error={errors.name} hint="Shown as the Signal title and in notifications.">
              {(p) => <input {...p} className="input" value={d.name} onChange={(e) => set({ name: e.target.value })} placeholder="Checkout latency is high" autoFocus />}
            </Field>
            <div className="field-row">
              <Field label="Measure" error={errors.aggregation}>
                {(p) => (
                  <select {...p} className="input" value={d.aggregation} onChange={(e) => set({ aggregation: e.target.value as MetricAgg })}>
                    {aggs.map((a) => <option key={a} value={a}>{AGG_LABEL[a]}</option>)}
                  </select>
                )}
              </Field>
              <Field label="Service" error={errors.service}>
                {(p) => (
                  <select {...p} className="input" value={d.service} onChange={(e) => set({ service: e.target.value })}>
                    <option value="">All services</option>
                    {services.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
              </Field>
            </div>
            <div className="field-row">
              <Field label="Alert when it goes" error={errors.comparator}>
                {(p) => (
                  <select {...p} className="input" value={d.comparator} onChange={(e) => set({ comparator: e.target.value as "above" | "below" })}>
                    <option value="above">Above</option>
                    <option value="below">Below</option>
                  </select>
                )}
              </Field>
              <Field label="Threshold" error={errors.threshold} hint={info?.unit && unitName(info.unit) ? `In ${unitName(info.unit)}.` : undefined}>
                {(p) => <input {...p} className="input" inputMode="decimal" value={d.threshold} onChange={(e) => set({ threshold: e.target.value })} placeholder="Enter a number" />}
              </Field>
            </div>
            <div className="field-row">
              <Field label="For at least" error={errors.windowMin} hint="Measured over this many minutes.">
                {(p) => (
                  <select {...p} className="input" value={d.windowMin} onChange={(e) => set({ windowMin: Number(e.target.value) })}>
                    {WINDOWS.map((w) => <option key={w} value={w}>{w === 1 ? "1 minute" : `${w} minutes`}</option>)}
                  </select>
                )}
              </Field>
              <Field label="Level" error={errors.severity}>
                {(p) => (
                  <select {...p} className="input" value={d.severity} onChange={(e) => set({ severity: e.target.value as AlertLevel })}>
                    {ALERT_LEVELS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                  </select>
                )}
              </Field>
            </div>
          </>
        )}
      </Dialog>
      <ConfirmDialog
        open={confirmDiscard}
        danger
        typeToConfirm={false}
        title="Discard changes"
        confirmLabel="Discard"
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          setServerErr({});
          setEditing(null);
        }}
      >
        <p style={{ margin: 0 }}>This alert has changes you have not saved.</p>
      </ConfirmDialog>
    </>
  );
}

function StateBadge({ r }: { r: MetricRule }) {
  if (!r.enabled) return <span className="badge neutral">Paused</span>;
  if (r.breached) return <span className="badge danger">Firing</span>;
  if (!r.lastCheckedAt) return <span className="badge neutral">Waiting for data</span>;
  return <span className="badge ok">OK</span>;
}

/** Every metric alert in the project. Owners and admins edit them. */
export function MetricAlerts({ metrics, onEdit }: { metrics: MetricInfo[]; onEdit: (e: AlertEditing) => void }) {
  const auth = useAuth();
  const can = useCan();
  const qc = useQueryClient();
  const toast = useToast();
  const [deleting, setDeleting] = useState<MetricRule | null>(null);
  const q = useQuery({ queryKey: backendKeys.metricRules(auth.currentProjectId), queryFn: () => backendApi.metricRules(auth.currentProjectId!), enabled: !!auth.currentProjectId });
  const invalidate = () => qc.invalidateQueries({ queryKey: backendKeys.metricRules(auth.currentProjectId) });
  const toggle = useMutation({
    mutationFn: (r: MetricRule) => backendApi.updateMetricRule(auth.currentProjectId!, r.id, { ...r, enabled: !r.enabled }),
    onSuccess: (_x, r) => { void invalidate(); toast.push("success", r.enabled ? "Alert paused" : "Alert resumed"); },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const del = useMutation({
    mutationFn: (r: MetricRule) => backendApi.deleteMetricRule(auth.currentProjectId!, r.id),
    onSuccess: () => { setDeleting(null); void invalidate(); toast.push("success", "Alert deleted"); },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const rules = q.data?.rules ?? [];
  const unitOf = (name: string) => metrics.find((m) => m.name === name)?.unit;
  if (q.isSuccess && rules.length === 0 && !can.admin) return null;

  return (
    <section className="card section be-alerts" aria-label="Metric alerts">
      <h2 className="be-h2">Metric alerts</h2>
      {q.isError ? (
        <ErrorState error={q.error} retry={() => void q.refetch()} />
      ) : q.isSuccess && rules.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>No alerts yet. Choose a metric, then Alert on this metric. An alert opens a Signal while the metric stays past its threshold.</p>
      ) : (
        <div className="stack-table be-table">
          <DataTable<MetricRule>
            ariaLabel="Metric alerts"
            rows={rules}
            rowKey={(r) => r.id}
            loading={q.isPending}
            columns={[
              { key: "name", header: "Alert", render: (r) => (<span className="cell-stack"><strong>{r.name}</strong><small className="mono">{r.metric}{r.service ? ` · ${r.service}` : ""}</small></span>) },
              { key: "cond", header: "When", label: "When", render: (r) => <span className="small">{conditionLabel(r, unitOf(r.metric))}</span> },
              { key: "level", header: "Level", label: "Level", width: 100, render: (r) => <span className="small">{ALERT_LEVELS.find((l) => l.id === r.severity)?.label ?? r.severity}</span> },
              { key: "state", header: "State", label: "State", width: 150, render: (r) => <StateBadge r={r} /> },
              { key: "last", header: "Latest value", label: "Latest value", width: 130, align: "right", render: (r) => <span className="mono small">{r.lastValue === undefined ? "—" : valueLabel(r.lastValue, r.aggregation, unitOf(r.metric))}</span> },
              ...(can.admin ? [{
                key: "act",
                header: "",
                align: "right" as const,
                width: 56,
                render: (r: MetricRule) => (
                  <RowMenu label={`Actions for ${r.name}`}>
                    <button type="button" role="menuitem" onClick={() => onEdit(editAlert(r))}>Edit alert</button>
                    <button type="button" role="menuitem" disabled={toggle.isPending} onClick={() => toggle.mutate(r)}>{r.enabled ? "Pause alert" : "Resume alert"}</button>
                    <button type="button" role="menuitem" className="danger" onClick={() => setDeleting(r)}>Delete alert</button>
                  </RowMenu>
                ),
              }] : []),
            ]}
          />
        </div>
      )}
      <ConfirmDialog
        open={!!deleting}
        danger
        typeToConfirm={false}
        title="Delete metric alert"
        confirmLabel="Delete alert"
        busy={del.isPending}
        onClose={() => { if (!del.isPending) setDeleting(null); }}
        onConfirm={() => deleting && !del.isPending && del.mutate(deleting)}
      >
        <p style={{ margin: 0 }}><strong>{deleting?.name}</strong> stops checking <span className="mono">{deleting?.metric}</span>. Signals it already opened stay in the list.</p>
      </ConfirmDialog>
    </section>
  );
}
